import { applyBrandThemeToSvg } from './brandTheme'
import { applyAxisDisplayFormatsToSvg } from './axisDisplayFormat'
import { applyAxisGuideLinesToSvg } from './axisGuideLines'
import { applyAxisDomainLineVisibilityToSvg } from './axisDomainLines'
import { cleanCrowdedXAxisLabels, cleanYAxisLabels } from './axisLabelCleanup'
import { applyAxisTitleLayoutToSvg } from './axisTitleLayout'
import { reduceBumpChartLabelOverlap } from './bumpChartLabelLayout'
import { getBumpLegendOrder, reorderLegendItems } from './legendLayout'
import { applyLegendPlacement } from './legendPlacement'
import { getSvgMetrics, fitSvgViewBoxToContents } from './brandTheme/svgMetrics'
import { releaseAxisFromClipPath } from './axisClipEscape'
import { CHART_IDS } from '../charts/chartIds'

/**
 * Applies the full post-render SVG transform sequence shared between preview
 * and export. Callers may pre-compute svgMetrics before the theme modifies the
 * SVG (important for export); pass null to compute it here (fine for preview).
 *
 * @param {SVGElement} svgNode
 * @param {object} options
 *   brandTheme, svgMetrics?, chartId, mapping, dataTypes, visualOptions,
 *   recolorMarks, legendOrder?
 */
function sanitizeNegativeDimensions(svgNode) {
  svgNode.querySelectorAll('rect').forEach((rect) => {
    const h = parseFloat(rect.getAttribute('height'))
    const w = parseFloat(rect.getAttribute('width'))
    if (Number.isFinite(h) && h < 0) rect.setAttribute('height', '0')
    if (Number.isFinite(w) && w < 0) rect.setAttribute('width', '0')
  })
}

// d3.pack produces NaN positions when chartWidth/chartHeight rounds to 0 or an
// option value is undefined. Strip NaN from transforms and geometric attrs before
// the rest of the pipeline runs so that getBBox/getBoundingClientRect calls on
// downstream steps don't receive garbage coordinates.
function sanitizeNaNAttributes(svgNode) {
  svgNode.querySelectorAll('[transform]').forEach((el) => {
    if ((el.getAttribute('transform') || '').includes('NaN')) {
      el.removeAttribute('transform')
    }
  })
  for (const attr of ['cx', 'cy', 'x', 'y', 'r', 'd']) {
    svgNode.querySelectorAll(`[${attr}]`).forEach((el) => {
      const val = el.getAttribute(attr) || ''
      if (val.includes('NaN')) el.setAttribute(attr, attr === 'd' ? '' : '0')
    })
  }
}

export function applySvgTransformPipeline(svgNode, options = {}) {
  if (!svgNode) return

  const {
    brandTheme,
    svgMetrics = null,
    chartId,
    mapping,
    dataTypes,
    visualOptions,
    recolorMarks = true,
    legendOrder = null,
    fitToContents = true,
  } = options

  const metrics = svgMetrics || getSvgMetrics(svgNode)

  sanitizeNegativeDimensions(svgNode)
  sanitizeNaNAttributes(svgNode)

  applyBrandThemeToSvg(svgNode, brandTheme, svgMetrics, {
    recolorMarks,
    colorScaleOption: visualOptions?.colorScale,
    chartId,
  })

  // Stamp original declared chart dimensions before any label-cleanup step
  // patches the background rect. chartAutoMargin reads these to detect real
  // bottom/right overflow even after ensureSvgBottomPadding has expanded things.
  const bgRectEl = svgNode.querySelector('[data-lugsail-theme-background="true"]')
  if (bgRectEl) {
    bgRectEl.dataset.lugsailBaseHeight = bgRectEl.getAttribute('height') || ''
    bgRectEl.dataset.lugsailBaseWidth  = bgRectEl.getAttribute('width')  || ''
  }

  // Some charts (e.g. line chart) place the axis group inside the same
  // clip-path container used for the data series.  Move the clip-path onto
  // only the data children so axis tick labels are never clipped.
  releaseAxisFromClipPath(svgNode)

  applyAxisDisplayFormatsToSvg(svgNode, { mapping, dataTypes, visualOptions, chartId })
  applyAxisGuideLinesToSvg(svgNode, metrics, chartId, {
    showVerticalAxisLines: visualOptions?.showVerticalAxisLines === true,
    showHorizontalAxisLines: visualOptions?.showHorizontalAxisLines === true,
  })
  applyAxisDomainLineVisibilityToSvg(svgNode, chartId, {
    showAxisDomainLine: visualOptions?.showAxisDomainLine !== false,
  })
  const xLabelResult = cleanCrowdedXAxisLabels(svgNode, { ...brandTheme, chartId })
  const yLabelResult = cleanYAxisLabels(svgNode, { ...brandTheme, chartId })
  applyAxisTitleLayoutToSvg(
    svgNode,
    getSvgMetrics(svgNode, {
      excludeSelectors: ['[data-lugsail-axis-guide-line="true"]'],
    }),
    chartId,
    {
      showAxisTitles: visualOptions?.showAxisTitles !== false,
      textColor: brandTheme?.textColor,
    }
  )
  if (chartId === CHART_IDS.BUMP_CHART) {
    reduceBumpChartLabelOverlap(svgNode)
    reorderLegendItems(svgNode, { chartId, legendOrder })
  }
  applyLegendPlacement(svgNode, brandTheme)
  if (fitToContents) {
    fitSvgViewBoxToContents(svgNode)
  }
  return { xLabelResult, yLabelResult }
}

export function buildBumpLegendOrder(rawViz, mapping) {
  return getBumpLegendOrder(rawViz?._mappedData, mapping)
}
